import { CheckCircle, CircleOutlined, ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	Divider,
	Fade,
	FormControlLabel,
	Typography,
	useTheme,
} from '@mui/material';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { addDays, format, parseISO } from 'date-fns';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { DISPLAY_DATE } from '@acorn-portal/shared/src/utils/constants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import { setAutoRenewalFlag } from '@acorn-portal/shared/src/store/fullPolicy.slice';
import { RenewalsClient } from '@acorn-portal/shared/src/services/renewalsApi';

interface IProps {
	autoRenewal: boolean;
	policyToken: string;
	expiryDate: string;
	isShortTermPolicy: boolean;
	cancellationRefundEligible?: boolean;
}

const optOutFormParagraphStyle = {
	my: '1rem',
};

const RenewalOptInForm: FC<IProps> = ({
	isShortTermPolicy,
	autoRenewal,
	policyToken,
	expiryDate,
	cancellationRefundEligible = true,
}) => {
	const [isLoading, setLoading] = useState(false);
	const [willAutoRenew, setAutoRenewal] = useState(autoRenewal);
	const [openOptOutExpand, toggleOptOutExpand] = useState(false);
	const [changeHasBeenMade, toggleChangeHasBeenMade] = useState(false);
	const [changeMessage, setChangeMessage] = useState('');
	const themeUtils = useTheme();
	const dispatch = useAppDispatch();
	const parseExpiryDate = addDays(parseISO(expiryDate), 1);
	const formatExpiryDate = format(parseExpiryDate, DISPLAY_DATE);
	const renewalAction = isShortTermPolicy ? 'roll over' : 'auto renew';

	const setRenewalStatus = async () => {
		try {
			setLoading(true);
			const client = new RenewalsClient(undefined, API);
			await client.setAutoRenewalStatus({
				policyToken,
				autoRenewal: willAutoRenew,
			});
			dispatch(setAutoRenewalFlag(willAutoRenew));
			setLoading(false);
			toggleChangeHasBeenMade(true);
			setChangeMessage(
				willAutoRenew
					? `Thank you, your policy will now ${renewalAction} on the ${formatExpiryDate}.`
					: `Your policy will no longer ${renewalAction} on the ${formatExpiryDate}.`,
			);
		} catch (error) {
			console.error('Error setting renewal status:', error);
			setLoading(false);
			toast('Unable to set renewal status. Please try again.', {
				position: 'top-right',
				autoClose: 3000,
				pauseOnHover: true,
				style: {
					background: themeUtils.palette.primary.main,
					color: themeUtils.palette.common.white,
				},
			});
		}
	};
	if (changeHasBeenMade) {
		return (
			<Box sx={{ mt: 4, mb: 2 }}>
				<Fade in timeout={500}>
					<Box sx={{ position: 'relative' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', sm: 'row' },
								alignItems: 'center',
								gap: 3,
							}}
						>
							{/* Success icon with animated effect */}
							<Box
								sx={(theme) => ({
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									backgroundColor: theme.palette.success.light,
									borderRadius: '50%',
									padding: 1.5,
									boxShadow: '0 6px 20px rgba(0, 200, 83, 0.15)',
									animation: 'pulse 2s infinite',
									'@keyframes pulse': {
										'0%': { boxShadow: '0 0 0 0 rgba(0, 200, 83, 0.4)' },
										'70%': { boxShadow: '0 0 0 10px rgba(0, 200, 83, 0)' },
										'100%': { boxShadow: '0 0 0 0 rgba(0, 200, 83, 0)' },
									},
								})}
							>
								<CheckCircleOutlineIcon
									sx={(theme) => ({
										fontSize: '3.5rem',
										color: theme.palette.common.white,
									})}
								/>
							</Box>

							{/* Content section */}
							<Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flex: 1 }}>
								<Typography
									variant='h6'
									sx={(theme) => ({
										color: theme.palette.text.primary,
										fontWeight: 600,
										mb: 1,
										letterSpacing: '0.015em',
									})}
								>
									Success!
								</Typography>
								<Typography
									variant='body1'
									sx={(theme) => ({
										color: theme.palette.text.secondary,
										lineHeight: 1.6,
									})}
								>
									{changeMessage}
								</Typography>
							</Box>
						</Box>

						{/* Decorative elements */}
						<Box
							sx={(theme) => ({
								position: 'absolute',
								bottom: 10,
								right: 10,
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								border: `1px dashed ${theme.palette.grey[200]}`,
								opacity: 0.5,
								zIndex: 0,
							})}
						/>
						<Box
							sx={(theme) => ({
								position: 'absolute',
								bottom: -10,
								right: 60,
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								border: `1px dashed ${theme.palette.grey[200]}`,
								opacity: 0.3,
								zIndex: 0,
							})}
						/>
					</Box>
				</Fade>
			</Box>
		);
	}

	return (
		<Box>
			<LoadingModal
				open={isLoading}
				message={`Setting ${isShortTermPolicy ? 'Rolling Subscription' : 'Renewal'} Status...`}
			/>
			{autoRenewal ? (
				<Box>
					<Accordion elevation={0} expanded={openOptOutExpand} sx={{ cursor: 'unset', boxShadow: 'unset' }}>
						<AccordionSummary
							sx={{ cursor: 'unset !important', padding: 0, span: { my: 0 } }}
							expandIcon={<ExpandMore />}
							aria-controls='renewal-opt-out-content'
							id='renewal-opt-out-header'
							onClick={() => toggleOptOutExpand(!openOptOutExpand)}
						>
							<Box>
								<Typography sx={optOutFormParagraphStyle}>
									To make things easier and for your continued protection, your policy is set to automatically{' '}
									{isShortTermPolicy ? 'roll over' : 'renew'}. You can change this at any time by clicking the{' '}
									{isShortTermPolicy ? 'Rolling Subscription' : 'Auto Renewal'} Opt-Out button{' '}
									<Box
										role='button'
										sx={{
											color: themeUtils.palette.primary.main,
											textDecoration: 'underline',
											cursor: 'pointer',
											'&:hover': {
												color: themeUtils.palette.primary.light,
											},
											'&:visited': {
												color: themeUtils.palette.primary.main,
												'&:hover': {
													color: themeUtils.palette.primary.light,
												},
											},
											verticalAlign: 'baseline',
										}}
										onClick={(e) => {
											e.stopPropagation();
											toggleOptOutExpand(!openOptOutExpand);
										}}
									>
										below
									</Box>
								</Typography>
								{!cancellationRefundEligible && (
									<Typography sx={optOutFormParagraphStyle}>
										If you decide to cancel your policy at any time, you will not receive a refund.
									</Typography>
								)}
							</Box>
						</AccordionSummary>
						<AccordionDetails sx={{ p: 0 }}>
							<Typography sx={optOutFormParagraphStyle}>
								Please note it is an offence under the Road Traffic Act 1988 to leave, drive or otherwise use your
								vehicle on a public highway without a valid insurance cover being in force, therefore if you do opt out
								please ensure you arrange for alternative cover on expiry of your policy.
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										icon={<CircleOutlined />}
										checkedIcon={<CheckCircle />}
										checked={willAutoRenew === false}
										id='renewal-opt-out'
										onChange={() => {
											setAutoRenewal(() => !willAutoRenew);
										}}
										sx={{
											'& .MuiSvgIcon-root': { fontSize: '4rem' },
										}}
									/>
								}
								label={`I would like to opt out of my ${
									isShortTermPolicy ? 'rolling subscription' : 'auto-renewal'
								} now`}
							/>
							<Divider />
							<Box
								sx={(theme) => ({
									marginTop: '20px',
									[theme.breakpoints.up('md')]: {
										maxWidth: '200px',
										marginLeft: 'auto',
									},
								})}
							>
								<Button
									color='primary'
									variant='contained'
									fullWidth
									onClick={setRenewalStatus}
									disabled={willAutoRenew === true}
								>
									Submit
								</Button>
							</Box>
						</AccordionDetails>
					</Accordion>
				</Box>
			) : (
				<Box>
					<Typography sx={optOutFormParagraphStyle}>
						You have opted out of {isShortTermPolicy ? 'Rolling Subscription' : 'Auto Renewal'}. This means your policy
						will expire on {format(new Date(expiryDate), DISPLAY_DATE)}. Please note that we
						{isShortTermPolicy
							? " won't collect any payments and won't set your new policy up "
							: " will send you a new quote about 3 weeks before your policy expires, but we won't renew your policy "}
						unless you opt in to {isShortTermPolicy ? 'Rolling Subscription' : 'Auto Renewal'} using the button below or
						contact us{isShortTermPolicy ? '' : ' to renew'}.
					</Typography>
					{!cancellationRefundEligible && (
						<Typography sx={optOutFormParagraphStyle}>
							If you decide to cancel your policy at any time, you will not receive a refund.
						</Typography>
					)}
					<Typography sx={optOutFormParagraphStyle}>
						Please note it is an offence under the Road Traffic Act 1988 to leave, drive or otherwise use your vehicle
						on a public highway without a valid insurance cover being in force, therefore if you{' '}
						{isShortTermPolicy ? 'choose not to accept the Rolling Subscription' : ' do not renew'} please ensure you
						arrange for alternative cover on expiry of your policy
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								icon={<CircleOutlined />}
								checkedIcon={<CheckCircle />}
								checked={willAutoRenew === true}
								id='renewal-opt-in'
								onChange={() => {
									setAutoRenewal(() => !willAutoRenew);
								}}
								sx={{
									'& .MuiSvgIcon-root': { fontSize: '4rem' },
								}}
							/>
						}
						label={`I would like my cover to continue past ${format(new Date(expiryDate), DISPLAY_DATE)}`}
					/>
					<Divider />
					<Box
						sx={(theme) => ({
							marginTop: '20px',
							[theme.breakpoints.up('md')]: {
								maxWidth: '200px',
								marginLeft: 'auto',
							},
						})}
					>
						<Button
							color='primary'
							variant='contained'
							fullWidth
							onClick={setRenewalStatus}
							disabled={willAutoRenew === false}
						>
							Submit
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default RenewalOptInForm;
